import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Popover,
  TextField,
  Box,
  Typography,
  Collapse,
  Divider,
  CircularProgress,
  Tooltip,

} from "@mui/material";

import {
  Edit,
  Delete as DeleteIcon,
  ExpandMore,
  ExpandLess,
  Description as FileIcon,
  Visibility
} from "@mui/icons-material";
import AddIcon from "@mui/icons-material/Add";
import { useNavigate } from "react-router-dom";
import InfiniteScroll from "react-infinite-scroll-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useMediaQuery, useTheme } from "@mui/material";
import { debounce } from "lodash";

import {
  getAllMTC,
  deleteMTC,
  exportMTCExcel,
} from "../../../Services/Services";

export default function MtcList() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [mtcList, setMtcList] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [expandedItem, setExpandedItem] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  // Filters - Only TC No and Heat No
  const [filters, setFilters] = useState({
    tc_no: "",
    heat_no: "",
    size: "",
  });

  // Delete confirmation popover
  const [anchorEl, setAnchorEl] = useState(null);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Check if any filter applied
  const isAnyFilterApplied = () => {
    return filters.tc_no.trim() !== "" || filters.heat_no.trim() !== "" || filters.size.trim() !== "";
  };

  // Fetch MTC data
  const fetchMTC = async (pageNum = 1, append = false) => {
    try {
      const apiFilters = {
        tc_no: filters.tc_no,
        heat_no: filters.heat_no,
        size: filters.size, 
      };

      const data = await getAllMTC(pageNum, apiFilters);

      if (append) {
        setMtcList((prev) => [...prev, ...data.results]);
      } else {
        setMtcList(data.results || []);
      }

      setHasMore(!!data.next);
    } catch (err) {
      console.error("Failed to fetch MTC:", err);
      toast.error("Failed to load MTC list");
    }
  };

  // Debounced filter effect
  useEffect(() => {
    const handler = setTimeout(() => {
      setPage(1);
      fetchMTC(1, false);
    }, 1000);

    return () => clearTimeout(handler);
  }, [filters]);

  // Infinite scroll
  const fetchMoreData = () => {
    if (hasMore) {
      const nextPage = page + 1;
      fetchMTC(nextPage, true);
      setPage(nextPage);
    }
  };

  // Delete handler
  const handleDelete = async (id) => {
    try {
      await deleteMTC(id);
      setMtcList((prev) => prev.filter((item) => item.id !== id));
      toast.success("Deleted successfully");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Failed to delete");
    }
  };

  // Excel export
  const handleExcelExport = async () => {
    setExcelLoading(true);
    try {
      const apiFilters = {
        tc_no: filters.tc_no,
        heat_no: filters.heat_no,
      };

      const blob = await exportMTCExcel(apiFilters);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `MTC_List_${new Date().toISOString().split("T")[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);

      toast.success("Excel downloaded successfully!");
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export Excel");
    } finally {
      setExcelLoading(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isMobile ? "auto" : "hidden";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isMobile]);

  return (
    <>
      <ToastContainer position="top-right" autoClose={3000} />

      {/* Filters & Buttons */}
      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{
          mt: -3,
          pt: -3,
          mb: 1,
          width: "100%",
          gap: 2,
        }}
      >
        {/* TC No */}
        <TextField
          label="TC No."
          value={filters.tc_no}
          onChange={(e) => setFilters({ ...filters, tc_no: e.target.value })}
          size="small"
          sx={{
            width: { xs: "100%", sm: "225px" },
            "& .MuiInputBase-root": {
              height: 30,
              fontSize: "0.7rem",
              borderRadius: "0px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",
            },
          }}
        />

        {/* Heat No */}
        <TextField
          label="Heat No."
          value={filters.heat_no}
          onChange={(e) => setFilters({ ...filters, heat_no: e.target.value })}
          size="small"
          sx={{
            width: { xs: "100%", sm: "225px" },
            "& .MuiInputBase-root": {
              height: 30,
              fontSize: "0.7rem",
              borderRadius: "0px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",
            },
          }}
        />
 {/* Heat No */}
        <TextField
          label="Size."
          value={filters.size}
          onChange={(e) => setFilters({ ...filters, size: e.target.value })}
          size="small"
          sx={{
            width: { xs: "100%", sm: "225px" },
            "& .MuiInputBase-root": {
              height: 30,
              fontSize: "0.7rem",
              borderRadius: "0px",
            },
            "& .MuiInputLabel-root": {
              fontSize: "0.7rem",
            },
          }}
        />
        {/* Right side buttons */}
        <Box
          display="flex"
          flexDirection={{ xs: "column", sm: "row" }}
          gap={2}
          ml={{ xs: 0, sm: "auto" }}
          width={{ xs: "100%", sm: "auto" }}
        >
          {/* Add MTC Button */}
          <Button
            variant="contained"
            color="primary"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 18 }} />}
            fullWidth={{ xs: true, sm: false }}
            onClick={() =>
              navigate("/dashboard/mtc-form", {
                state: { mode: "create" },
              })
            }
            sx={{
              height: 30,
              fontSize: "0.7rem",
              padding: "2px 15px",
              borderRadius: "0px",
            }}
          >
            MTC
          </Button>

          {/* Excel Export */}
          <Tooltip
            title={excelLoading ? "Exporting..." : "Download Excel"}
            arrow
          >
            <span>
              <IconButton
                color="success"
                size="small"
                onClick={handleExcelExport}
                disabled={excelLoading}
                sx={{
                  borderRadius: "4px",
                  padding: "6px",
                  border: "1px solid #4caf50",
                  "&:hover": {
                    backgroundColor: "#e8f5e9",
                  },
                }}
              >
                {excelLoading ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <FileIcon fontSize="small" />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>

      {/* Infinite Scroll */}
      <InfiniteScroll
        dataLength={mtcList.length}
        next={fetchMoreData}
        hasMore={hasMore}
        loader={<h4>Loading...</h4>}
        endMessage={
          <Typography sx={{ textAlign: "center", py: 2, color: "#999" }}>
            No more MTC records!
          </Typography>
        }
      >
        {isMobile ? (
          /* 📱 MOBILE VIEW - Expandable Cards */
          <Box sx={{ bgcolor: "background.paper" }}>
            {mtcList.map((item, index) => (
              <Box key={item.id || index}>
                {/* Main Row */}
                <Box
                  onClick={() =>
                    setExpandedItem(expandedItem === item.id ? null : item.id)
                  }
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 1.5,
                    cursor: "pointer",
                    bgcolor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                    "&:active": {
                      bgcolor: "#e3f2fd",
                    },
                    borderLeft: "4px solid #1976d2",
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    {/* S.No + TC No */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 0.5,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#666",
                          minWidth: 25,
                        }}
                      >
                        {index + 1}
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: "bold",
                          fontSize: "0.85rem",
                          flex: 1,
                        }}
                      >
                        {item.tc_no || "N/A"}
                      </Typography>
                    </Box>

                    {/* Material */}
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        color: "#666",
                        ml: 4,
                      }}
                    >
                      {item.material?.substring(0, 30) || "N/A"}
                    </Typography>
                  </Box>

                  <IconButton size="small" sx={{ ml: 1 }}>
                    {expandedItem === item.id ? (
                      <ExpandLess fontSize="small" />
                    ) : (
                      <ExpandMore fontSize="small" />
                    )}
                  </IconButton>
                </Box>

                {/* Expanded Details */}
                <Collapse
                  in={expandedItem === item.id}
                  timeout="auto"
                  unmountOnExit
                >
                  <Box
                    sx={{
                      px: 1.5,
                      py: 1,
                      bgcolor: index % 2 === 0 ? "#f8f9fa" : "#fafafa",
                      borderLeft: "4px solid #1976d2",
                    }}
                  >
                    {/* 2 Column Grid */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: 0.8,
                        mb: 1,
                      }}
                    >
                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          SIZE
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.size || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          TC NO.
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.tc_no || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          HEAT NO.
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.heat_no || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          TUBE QTY (NOS)
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.tube_qty || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          UOM
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.uom || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          PER MATCH LENGTH
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.per_tube_length || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography
                          sx={{
                            fontSize: "0.6rem",
                            color: "#999",
                            letterSpacing: "0.5px",
                          }}
                        >
                          QTY(m)
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: "0.72rem",
                            fontWeight: 500,
                            color: "#555",
                          }}
                        >
                          {item.qty || "N/A"}
                        </Typography>
                      </Box>

                      <Box>
                        <Typography sx={{ fontSize: "0.6rem", color: "#999", letterSpacing: "0.5px" }}>
                          ISSUE QTY
                        </Typography>
                        <Typography sx={{ fontSize: "0.72rem", fontWeight: 500, color: "#555" }}>
                          {item.issue_qty || 0.0}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Material - Full Width */}
                    <Box sx={{ mb: 0.8 }}>
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          letterSpacing: "0.5px",
                          mb: 0.3,
                        }}
                      >
                        MATERIAL
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "0.7rem",
                          color: "#555",
                          lineHeight: 1.3,
                          fontWeight: 500,
                        }}
                      >
                        {item.material || "N/A"}
                      </Typography>
                    </Box>

                    <Divider sx={{ my: 1, borderColor: "#e0e0e0" }} />

                    {/* Actions */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 1,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "0.6rem",
                          color: "#999",
                          fontWeight: 600,
                          letterSpacing: "0.5px",
                        }}
                      >
                        ACTIONS
                      </Typography>

                      <Box sx={{ display: "flex", gap: 0.6 }}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate("/dashboard/mtc-form", {
                              state: { mode: "edit", data: item },
                            });
                          }}
                          sx={{
                            bgcolor: "#e3f2fd",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#bbdefb",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <Edit sx={{ fontSize: "1rem", color: "#1976d2" }} />
                        </IconButton>

                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            setAnchorEl(e.currentTarget);
                            setItemToDelete(item.id);
                          }}
                          sx={{
                            bgcolor: "#ffebee",
                            borderRadius: "6px",
                            width: 32,
                            height: 32,
                            "&:hover": {
                              bgcolor: "#ffcdd2",
                              transform: "scale(1.05)",
                            },
                            transition: "all 0.2s",
                          }}
                        >
                          <DeleteIcon
                            sx={{ fontSize: "1rem", color: "#d32f2f" }}
                          />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Collapse>

                <Divider />
              </Box>
            ))}
          </Box>
        ) : (
          /* 💻 DESKTOP VIEW - Table */
          <TableContainer
            component={Paper}
            sx={{
              maxHeight: "88vh",
              overflowX: "auto",
              overflowY: "auto",
              width: "100%",
            }}
          >
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 1200,
                "& td, & th": {
                  textAlign: "center",
                  whiteSpace: "nowrap",
                  borderBottom: "1px solid #ccc",
                },
              }}
            >
              <TableHead
                sx={{
                  "& th": {
                    fontWeight: "bold",
                    fontSize: "0.75rem",
                    color: (theme) =>
                      theme.palette.mode === "dark" ? "#2196f3" : "#333",
                    backgroundColor: (theme) =>
                      theme.palette.mode === "dark" ? "black" : "#f5f5f5",
                  },
                }}
              >
                <TableRow>
                  <TableCell
                    sx={{
                      position: "sticky",
                      left: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      minWidth: 60,
                    }}
                  >
                    S.No
                  </TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Size</TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Material</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>TC No.</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Heat No.</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>UOM</TableCell>
                  <TableCell sx={{ minWidth: 120 }}>Tube Qty (Nos)</TableCell>

                  <TableCell sx={{ minWidth: 140 }}>Per Match Length</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Qty(MTR)</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Issue Qty</TableCell>
                  <TableCell sx={{ minWidth: 100 }}>Year</TableCell>

                  <TableCell
                    align="center"
                    sx={{
                      position: "sticky",
                      right: 0,
                      backgroundColor: "#f5f5f5",
                      zIndex: 3,
                      minWidth: 100,
                    }}
                  >
                    Action
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {mtcList.map((item, index) => (
                  <TableRow
                    hover
                    key={item.id || index}
                    sx={{
                      backgroundColor: index % 2 === 0 ? "#fafafa" : "#ffffff",
                      "&:hover": {
                        backgroundColor: "#e3f2fd",
                        "& td": { backgroundColor: "#e3f2fd !important" },
                      },
                      "& td": {
                        paddingRight: "13px !important",
                        paddingLeft: "13px !important",
                        padding: "0px",
                        fontSize: { xs: "0.65rem", sm: "0.75rem" },
                        whiteSpace: "nowrap",
                        color: (theme) =>
                          theme.palette.mode === "dark" ? "white" : "inherit",
                        backgroundColor: (theme) =>
                          theme.palette.mode === "dark" ? "black" : "inherit",
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: "#fafafa",
                        zIndex: 2,
                      }}
                    >
                      {index + 1}
                    </TableCell>
                    <TableCell>{item.size || "N/A"}</TableCell>
                    <TableCell>{item.material || "N/A"}</TableCell>
                    <TableCell>{item.tc_no || "N/A"}</TableCell>
                    <TableCell>{item.heat_no || "N/A"}</TableCell>
                    <TableCell>{item.uom || "N/A"}</TableCell>
                    <TableCell>{item.tube_qty || "N/A"}</TableCell>

                    <TableCell>{item.per_tube_length || "N/A"}</TableCell>
                    <TableCell>{item.qty || "N/A"}</TableCell>
                    <TableCell>{item.issue_qty || 0.0}</TableCell>
                    <TableCell>{item.year || "N/A"}</TableCell>
                    <TableCell
                      align="center"
                      sx={{
                        position: "sticky",
                        right: 0,
                        backgroundColor: "#fafafa",
                        zIndex: 2,
                      }}
                    >
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        size="small"
                        color="primary"
                        onClick={() =>
                          navigate("/dashboard/mtc-form", {
                            state: { mode: "edit", data: item },
                          })
                        }
                      >
                        <Edit transform="scale(0.6)" />
                      </IconButton>
                      <IconButton
                        sx={{ padding: 0, paddingLeft: 1, paddingRight: 1 }}
                        size="small"
                        color="error"
                        onClick={(e) => {
                          setAnchorEl(e.currentTarget);
                          setItemToDelete(item.id);
                        }}
                      >
                        <DeleteIcon transform="scale(0.6)" />
                      </IconButton>
                      <span
                        style={{
                          display: "inline-block",
                          width: 36,
                          textAlign: "center",
                        }}
                      >
                        {typeof item.file === "string" ? (
                          <a
                            href={`${item.file}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                              }}
                              color="info"
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </a>
                        ) : item.file instanceof File ? (
                          <a
                            href={URL.createObjectURL(item.file)}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            <IconButton
                              sx={{
                                padding: 0,
                                paddingLeft: 1,
                                paddingRight: 1,
                              }}
                              color="info"
                              size="small"
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </a>
                        ) : (
                          <span
                            style={{ display: "inline-block", width: 24 }}
                          />
                        )}
                      </span>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </InfiniteScroll>

      {/* Delete Confirmation Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => {
          setAnchorEl(null);
          setItemToDelete(null);
        }}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">Are you sure?</Typography>
          <Box display="flex" justifyContent="flex-end" mt={1} gap={1}>
            <Button
              color="error"
              size="small"
              onClick={async () => {
                await handleDelete(itemToDelete);
                setAnchorEl(null);
                setItemToDelete(null);
              }}
            >
              Yes
            </Button>
            <Button size="small" onClick={() => setAnchorEl(null)}>
              No
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
}